import React, { useState, useEffect } from 'react';
import { getStaticVehiclesAnnotationSummary, getStaticUnprocessedImages } from '../services/staticData';
import './AnnotationsView.css';

// Component to handle image loading with HTTP/HTTPS fallback
const ImageWithFallback = ({ src, alt, onError, onLoad }) => {
  const [imageSrc, setImageSrc] = useState(src);
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    if (!hasError && src && src.startsWith('http://') && window.location.protocol === 'https:') {
      // Try HTTPS version if HTTP failed
      console.log('HTTP failed, trying HTTPS version');
      setImageSrc(src.replace('http://', 'https://'));
      setHasError(true);
    } else {
      console.error('All image loading attempts failed');
      onError && onError();
    }
  };

  const handleLoad = () => {
    onLoad && onLoad();
  };

  return (
    <img 
      src={imageSrc}
      alt={alt}
      onError={handleError}
      onLoad={handleLoad}
    />
  );
};

const AnnotationsView = ({ onImageSelect }) => {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');


  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await getStaticVehiclesAnnotationSummary();
      if (response.success && Array.isArray(response.vehicles)) {
        setVehicles(response.vehicles);
      } else {
        setError('Invalid data format received');
      }
    } catch (err) {
      console.error('Error fetching vehicles annotation summary:', err);
      setError(err.message || 'Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleSelect = async (vehicle) => {
    setSelectedVehicle(vehicle);
    await fetchImagesForVehicle(vehicle.vehicle_id);
  };

  const fetchImagesForVehicle = async (vehicleId) => {
    try {
      setLoading(true);
      setError('');
      const response = await getStaticUnprocessedImages(vehicleId, 50);
      console.log('Unprocessed images API response (static):', response);
      console.log('Response type:', typeof response);
      console.log('Is array:', Array.isArray(response));
      
      // Static data is already in the correct format
      let imagesData = [];
      if (Array.isArray(response)) {
        imagesData = response;
        console.log('Using response as array, length:', imagesData.length);
      } else {
        console.warn('Unexpected response format for unprocessed images:', response);
        imagesData = [];
      }
      
      setImages(imagesData);
    } catch (err) {
      console.error('Error fetching unprocessed images:', err);
      setError(err.message || 'Failed to load images');
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (image) => {
    onImageSelect(image);
  };

  if (loading && vehicles.length === 0) {
    return <div className="loading">Loading vehicles...</div>;
  }

  if (error && vehicles.length === 0) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="annotations-view">
      <div className="annotations-header">
        <h1>Annotations</h1>
        <p>Select a vehicle to view its annotation images</p>
      </div>

      <div className="annotations-content">
        <div className="vehicles-panel">
          <h3>Vehicles</h3>
          <div className="vehicles-list">
            {vehicles.map((vehicle) => (
              <div
                key={vehicle.vehicle_id}
                className={`vehicle-item ${selectedVehicle?.vehicle_id === vehicle.vehicle_id ? 'selected' : ''}`}
                onClick={() => handleVehicleSelect(vehicle)}
              >
                <span className="vehicle-icon">🚗</span>
                <div className="vehicle-info">
                  <div className="vehicle-name">{vehicle.vehicle_name}</div>
                  <div className="vehicle-id">ID: {vehicle.vehicle_id}</div>
                  <div className="device-id">Device: {vehicle.device_id}</div>
                  
                  <div className="annotation-stats">
                    <div className="stat-row">
                      <span className="stat-label">📊 Progress:</span>
                      <span className="stat-value">{vehicle.summary.annotation_progress.toFixed(1)}%</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">📝 Annotated:</span>
                      <span className="stat-value annotated">{vehicle.summary.annotated_images}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">⏳ Unprocessed:</span>
                      <span className="stat-value unprocessed">{vehicle.summary.unprocessed_images}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">📸 Total:</span>
                      <span className="stat-value total">{vehicle.summary.total_images}</span>
                    </div>
                  </div>

                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${vehicle.summary.annotation_progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="images-panel">
          {selectedVehicle ? (
            <>
              <div className="images-header">
                <h3>Unprocessed Images for {selectedVehicle.vehicle_name}</h3>
                <div className="image-count-info">
                  <span className="image-count">{images.length} unprocessed images</span>
                  <div className="camera-breakdown">
                    <span className="camera-stat">Front: {selectedVehicle.camera_breakdown.front.unprocessed}</span>
                    <span className="camera-stat">Rear: {selectedVehicle.camera_breakdown.rear.unprocessed}</span>
                    <span className="camera-stat">Side: {selectedVehicle.camera_breakdown.side.unprocessed}</span>
                  </div>
                </div>
              </div>
              
              {loading ? (
                <div className="loading">Loading images...</div>
              ) : error ? (
                <div className="error">{error}</div>
              ) : (
                <div className="images-grid">
                  {Array.isArray(images) ? images.map((image) => (
                    <div
                      key={image.id}
                      className="image-item"
                      onClick={() => handleImageClick(image)}
                    >
                      <div className="image-thumbnail">
                        {image.signed_url || image.image_url ? (
                          <ImageWithFallback 
                            src={image.signed_url || image.image_url}
                            alt={`Annotation ${image.id}`}
                            onError={() => console.warn(`Failed to load image: ${image.signed_url || image.image_url}`)}
                            onLoad={() => console.log(`Successfully loaded image: ${image.signed_url || image.image_url}`)}
                          />
                        ) : null}
                        <div className="image-placeholder" style={{ display: (image.signed_url || image.image_url) ? 'none' : 'flex' }}>
                          <span>📷</span>
                          <div className="placeholder-text">
                            {(image.signed_url || image.image_url) ? 'Failed to load' : 'No image'}
                          </div>
                        </div>
                      </div>
                      <div className="image-info">
                        <div className="image-id">ID: {image.id}</div>
                        <div className="image-status">
                          {image.annotations && image.annotations.length > 0 ? (
                            <span className="annotated">✅ Annotated</span>
                          ) : (
                            <span className="unprocessed">📝 Unprocessed</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="no-images">
                      <p>No unprocessed images found</p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="no-selection">
              <div className="no-selection-icon">📁</div>
              <h3>Select a Vehicle</h3>
              <p>Choose a vehicle from the list to view its unprocessed annotation images</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnotationsView;
